/**
 * @fileoverview LetsBlur - Main Application Component
 *
 * This is the root component for the LetsBlur AI Privacy Protection application.
 * It orchestrates the multi-step workflow for uploading, configuring, previewing,
 * and downloading blurred images.
 *
 * @description
 * The app follows a 4-step workflow:
 * - Step 0 (Upload): User uploads images
 * - Step 1 (Configure): AI detects faces/bodies, user configures blur settings
 * - Step 2 (Preview): User previews blurred result with watermarks
 * - Step 3 (Download): User downloads final blurred images
 *
 * @requires React
 * @requires ./components/* - UI components for each step
 * @requires ./utils/constants - API URL and blur level configurations
 * @requires ./utils/blurUtils - Download utility functions
 */
import React, { useState, useCallback, useEffect } from 'react';

/* ==========================================================================
   Component Imports
   ========================================================================== */
import ToastContainer from './components/ToastContainer';
import StepIndicator from './components/StepIndicator';
import SingleClickBlurStepIndicator from './components/SingleClickBlurStepIndicator';
import UploadStep from './components/UploadStep';
import ConfigureStep from './components/ConfigureStep';
import PreviewStep from './components/PreviewStep';
import DownloadStep from './components/DownloadStep';
import ContactModal from './components/ContactModal';
import ProcessingOverlay from './components/ProcessingOverlay';
import OneClickBlur from './components/OneClickBlur';

/* Video Components */
import VideoTrimStep from './components/VideoTrimStep';
import VideoConfigureStep from './components/VideoConfigureStep';
import VideoPreviewStep from './components/VideoPreviewStep';

/* ==========================================================================
   Utility Imports
   ========================================================================== */
import { API_URL, BLUR_LEVELS, STEPS, VIDEO_STEPS } from './utils/constants';
import { downloadFile } from './utils/blurUtils';
import { videoAPI, isVideoFile } from './utils/videoUtils';

/**
 * Main Application Component
 *
 * @returns {JSX.Element} The rendered application
 */
export default function App() {
  /* ========================================================================
     State Management
     ======================================================================== */

  /**
   * Workflow State
   * Controls the current step in the multi-step process (0-3)
   */
  const [step, setStep] = useState(0);

  /**
   * File Management State
   * @property {Array} files - Array of file objects with detection data and blur settings
   * @property {number} currentFileIndex - Index of the currently selected file
   */
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  /**
   * Processing State
   * Tracks async operations and their status messages
   */
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');

  /**
   * Bulk Action State
   * Settings for applying blur to multiple persons at once
   */
  const [bulkGender, setBulkGender] = useState(null);
  const [bulkLevel, setBulkLevel] = useState('high');
  const [bulkTarget, setBulkTarget] = useState('face');

  /**
   * Download Tracking State
   * Monitors download progress and completion status
   */
  const [downloadCount, setDownloadCount] = useState(0);
  const [allDownloaded, setAllDownloaded] = useState(false);

  /**
   * Feature Flags State
   * Tracks one-time use features like reanalysis
   */
  const [reanalyzeUsed, setReanalyzeUsed] = useState({});

  /**
   * UI State
   * Controls toast notifications and modal visibility
   */
  const [toasts, setToasts] = useState([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [hasAppliedBlur, setHasAppliedBlur] = useState(false);

  /**
   * Single Click Blur State
   * Tracks processing and completion state
   */
  const [isSingleClickBlur, setIsSingleClickBlur] = useState(false);
  const [oneClickComplete, setOneClickComplete] = useState(false);
  const [oneClickStats, setOneClickStats] = useState({ detected: 0, blurred: 0 });

  /**
   * Session & Config State
   * Manages user session and backend configuration
   */
  const [userSessionId, setUserSessionId] = useState(null);
  const [remainingUploads, setRemainingUploads] = useState(null);
  const [config, setConfig] = useState({
    max_image_size_mb: 15,
    max_video_size_mb: 20,
    max_files_per_upload: 2,
    max_files_per_day: 6,
    supported_image_formats: ['jpg', 'jpeg', 'png', 'webp', 'bmp'],
    supported_video_formats: ['mp4', 'mov', 'avi']
  });

  /**
   * Upload Mode State
   * Controls whether we're processing images or videos
   */
  const [uploadMode, setUploadMode] = useState('images'); // 'images' | 'videos'
  const isVideoMode = uploadMode === 'videos';

  /* ========================================================================
     Effects - Initialization & Side Effects
     ======================================================================== */

  /**
   * Reset bulk settings when returning to Upload step
   * Ensures clean state for new upload sessions
   */
  useEffect(() => {
    if (step === 0) {
      setBulkGender(null);
      setBulkLevel('high');
      setBulkTarget('face');
      setHasAppliedBlur(false);
    }
  }, [step]);

  /**
   * Load configuration from backend on mount
   * Fetches upload limits and supported formats
   */
  useEffect(() => {
    const url = userSessionId
      ? `${API_URL}/config?user_session_id=${encodeURIComponent(userSessionId)}`
      : `${API_URL}/config`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        setConfig(data);
        if (data.remainingUploads !== undefined) {
          setRemainingUploads(data.remainingUploads);
        }
      })
      .catch(err => console.warn('Failed to load config, using defaults:', err));
  }, [userSessionId]);

  /**
   * Initialize or retrieve user session ID from localStorage
   * Creates a persistent session identifier for rate limiting
   */
  useEffect(() => {
    let sessionId = localStorage.getItem('userSessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('userSessionId', sessionId);
    }
    setUserSessionId(sessionId);
  }, []);

  /**
   * Log page visit analytics when user is on upload step
   */
  useEffect(() => {
    if (step === 0) {
      fetch(`${API_URL}/log-page-visit`, {
        method: 'POST'
      }).catch(err => console.error('Failed to log page visit:', err));
    }
  }, [step]);

  /* ========================================================================
     Utility Functions
     ======================================================================== */

  /**
   * Display a toast notification
   * @param {string} message - The message to display
   * @param {'success'|'error'|'info'} type - The type of notification
   */
  const showToast = useCallback((message, type = 'success', persistent = false) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, persistent }]);
  }, []);

  /* ========================================================================
     API Handlers - Face Detection
     ======================================================================== */

  /**
   * Detect faces in an uploaded image using the backend API
   *
   * @param {number} fileIndex - Index of the file to process
   * @param {boolean} enhanced - Whether to use enhanced detection (more accurate but slower)
   * @returns {Promise<{detections: Array, sessionId: string, fileId: string}>}
   */
  const detectFaces = async (fileIndex, enhanced = false) => {
    const file = files[fileIndex];
    if (!file) return [];

    try {
      // Upload file
      const formData = new FormData();
      formData.append('file', file.file);

      const uploadUrl = userSessionId
        ? `${API_URL}/upload?user_session_id=${encodeURIComponent(userSessionId)}`
        : `${API_URL}/upload`;

      const uploadRes = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!uploadRes.ok) {
        const errorData = await uploadRes.json().catch(() => ({ detail: 'Upload failed' }));
        if (uploadRes.status === 429) {
          // Daily limit reached - show persistent toast and return special flag
          showToast(errorData.detail || 'Daily upload limit reached. Please try again tomorrow.', 'error', true);
          return { detections: [], sessionId: null, fileId: null, dailyLimitReached: true };
        }
        throw new Error(errorData.detail || 'Upload failed');
      }
      const uploadData = await uploadRes.json();
      const { sessionId, fileId, remainingUploads: remaining } = uploadData;

      if (remaining !== undefined) {
        setRemainingUploads(remaining);
      }

      // Detect faces
      const detectRes = await fetch(`${API_URL}/detect/${sessionId}/${fileId}?enhanced=${enhanced}`, {
        method: 'POST'
      });

      if (!detectRes.ok) throw new Error('Detection failed');
      const { detections } = await detectRes.json();

      return { detections, sessionId, fileId };
    } catch (err) {
      console.error('API detection failed, using fallback:', err);
      showToast('Unable to detect faces. Please press "Back" and try again. If problem persists, please contact us or use "Add Custom Blur Box" feature to blur objects.', 'error', true);
      return { detections: [], sessionId: null, fileId: null };
    }
  };

  /* ========================================================================
     Processing Handlers - File Analysis
     ======================================================================== */

  /**
   * Process all uploaded files through face detection
   * Iterates through files and runs AI detection on each
   */
  const processFiles = async () => {
    setIsProcessing(true);

    // Handle video uploads differently
    if (isVideoMode) {
      for (let i = 0; i < files.length; i++) {
        setProcessingStatus(`Uploading video ${i + 1}/${files.length}...`);

        try {
          const uploadResult = await videoAPI.upload(files[i].file, userSessionId);

          setFiles(prev => prev.map((f, idx) =>
            idx === i ? {
              ...f,
              sessionId: uploadResult.session_id,
              fileId: uploadResult.file_id,
              metadata: {
                duration_seconds: uploadResult.duration_seconds,
                fps: uploadResult.fps,
                width: uploadResult.width,
                height: uploadResult.height,
                frame_count: uploadResult.frame_count
              },
              resolutionLabel: uploadResult.resolution_label,
              maxBlurDuration: uploadResult.max_blur_duration,
              blurSettings: {},
              detections: []
            } : f
          ));
        } catch (err) {
          showToast(`Error uploading ${files[i].name}: ${err.message}`, 'error');
        }
      }

      setIsProcessing(false);
      setStep(1);
      showToast("Video uploaded. Detection will start automatically.", 'success');
      return;
    }

    // Handle image uploads (existing logic)
    let dailyLimitReached = false;
    for (let i = 0; i < files.length; i++) {
      setProcessingStatus(`Analyzing image ${i + 1}/${files.length}...`);

      try {
        const result = await detectFaces(i);

        // Check if daily limit was reached
        if (result.dailyLimitReached) {
          dailyLimitReached = true;
          break; // Stop processing
        }

        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, ...result, blurSettings: {}, customBlurBoxes: [] } : f
        ));
      } catch (err) {
        showToast(`Error processing ${files[i].name}`, 'error');
      }
    }

    setIsProcessing(false);

    // Don't proceed if daily limit was reached
    if (dailyLimitReached) {
      return;
    }

    setStep(1);
    // Show persistent toast to guide user on Configure page
    showToast("Use 'Quick Blur Actions' to select what to Blur/Skip.", 'success', true);
  };

  /**
   * Reanalyze a single image with enhanced detection
   * Can only be used once per image per session
   *
   * @param {number} index - Index of the file to reanalyze
   */
  const handleReanalyze = async (index) => {
    if (reanalyzeUsed[index]) return;

    setReanalyzeUsed(prev => ({ ...prev, [index]: true }));
    setIsProcessing(true);
    setProcessingStatus('Running enhanced detection...');

    try {
      const result = await detectFaces(index, true);

      setFiles(prev => prev.map((f, idx) =>
        idx === index ? { ...f, ...result, blurSettings: {}, customBlurBoxes: [] } : f
      ));

      showToast(`Found ${result.detections.length} person(s)`);
    } catch (err) {
      showToast('Enhanced detection failed', 'error');
    }

    setIsProcessing(false);
  };

  /* ========================================================================
     Download Handlers
     ======================================================================== */

  /**
   * Download a single processed file
   * @param {number} index - Index of the file to download
   */
  const handleDownload = (index) => {
    const file = files[index];

    // Handle video downloads
    if (isVideoMode && file.downloadUrl) {
      const link = document.createElement('a');
      link.href = file.downloadUrl;
      link.download = `${file.name.replace(/\.[^/.]+$/, '')}_blurred.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadCount(prev => prev + 1);
      showToast(`Downloaded: ${file.name}`);
      return;
    }

    // Handle image downloads
    downloadFile(
      file,
      (filename) => {
        setDownloadCount(prev => prev + 1);
        showToast(`Downloaded: ${filename}`);
      },
      (error) => showToast(error, 'error')
    );
  };

  /**
   * Download all processed files sequentially
   * Adds a delay between downloads to prevent browser throttling
   */
  const handleDownloadAll = () => {
    if (files.length === 0) return;

    showToast('Starting downloads...', 'info');

    let i = 0;
    const downloadNext = () => {
      if (i < files.length) {
        handleDownload(i);
        i++;
        setTimeout(downloadNext, 1200);
      }
    };
    downloadNext();
  };

  /**
   * Single Click Blur - Detect faces and download blurred images in one action
   * Detects all faces, applies high blur to all faces, and triggers download
   */
  const handleOneClickBlur = async () => {
    if (files.length === 0 || isVideoMode) return;

    // Clear all toasts before starting
    setToasts([]);

    setIsSingleClickBlur(true);
    setIsProcessing(true);
    setOneClickComplete(false);
    const processedFiles = [];
    let totalFaces = 0;
    let dailyLimitReached = false;

    // Step 1: Detect faces in all files
    for (let i = 0; i < files.length; i++) {
      setProcessingStatus(`Detecting faces in image ${i + 1}/${files.length}...`);

      try {
        const result = await detectFaces(i);

        // Check if daily limit was reached
        if (result.dailyLimitReached) {
          dailyLimitReached = true;
          // Stop processing, stay on step 0 (don't show completion overlay)
          setIsSingleClickBlur(false);
          setIsProcessing(false);
          return;
        }

        // Create blur settings for all detected faces (high blur on face)
        const blurSettings = {};
        (result.detections || []).forEach(detection => {
          blurSettings[detection.id] = {
            enabled: true,
            level: 'high',
            target: 'face'
          };
        });

        totalFaces += (result.detections || []).length;

        processedFiles.push({
          ...files[i],
          ...result,
          blurSettings,
          customBlurBoxes: []
        });
      } catch (err) {
        console.error(`Error processing ${files[i].name}:`, err);
        processedFiles.push({
          ...files[i],
          detections: [],
          blurSettings: {},
          customBlurBoxes: []
        });
      }
    }

    // Update files state with detection results
    setFiles(processedFiles);

    // Step 2: Download blurred images
    setProcessingStatus('Applying blur and preparing downloads...');

    // Small delay to ensure state is updated
    await new Promise(resolve => setTimeout(resolve, 100));

    let downloadedCount = 0;
    for (let i = 0; i < processedFiles.length; i++) {
      const file = processedFiles[i];

      // Only download if there are detections to blur
      if (file.detections && file.detections.length > 0) {
        await new Promise((resolve) => {
          downloadFile(
            file,
            (filename) => {
              downloadedCount++;
              resolve();
            },
            (error) => {
              console.error(`Download failed: ${error}`);
              resolve();
            }
          );
        });

        // Log Single Click Blur usage to backend
        if (file.sessionId && file.fileId) {
          fetch(`${API_URL}/log-page-visit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'single_click_blur',
              session_id: file.sessionId,
              file_id: file.fileId,
              filename: file.name
            })
          }).catch(err => console.error('Failed to log single click blur:', err));
        }

        // Delay between downloads
        if (i < processedFiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
      }
    }

    // Show completion state in overlay with stats
    setOneClickStats({
      detected: totalFaces,
      blurred: downloadedCount > 0 ? totalFaces : 0,
      files: processedFiles.length
    });
    setOneClickComplete(true);
    setIsProcessing(false);
  };

  /**
   * Handle Single Click Blur completion - refresh page
   */
  const handleOneClickComplete = () => {
    window.location.reload();
  };

  /* ========================================================================
     Navigation & State Reset Handlers
     ======================================================================== */

  /**
   * Reset the application to initial state
   * Cleans up object URLs and resets all state
   */
  const handleReset = () => {
    files.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
      if (f.thumbnail) URL.revokeObjectURL(f.thumbnail);
    });
    setFiles([]);
    setStep(0);
    setCurrentFileIndex(0);
    setDownloadCount(0);
    setReanalyzeUsed({});
    setAllDownloaded(false);
    setUploadMode('images');
  };

  /**
   * Navigate to the next step in the workflow
   * Triggers file processing when moving from Upload to Configure step
   * Clears any existing toast before navigation
   */
  const nextStep = () => {
    // Clear any existing toasts before moving to next step
    setToasts([]);

    if (step === 0 && files.length > 0) {
      processFiles();
    } else {
      // Video mode has 5 steps (0-4), image mode has 4 steps (0-3)
      const maxStep = isVideoMode ? 4 : 3;
      if (step < maxStep) {
        setStep(step + 1);
      }
    }
  };

  /**
   * Navigate to the previous step in the workflow
   * Clears any existing toast before navigation
   */
  const prevStep = () => {
    // Clear any existing toasts before moving to previous step
    setToasts([]);
    if (step > 0) setStep(step - 1);
  };

  /**
   * Check if user can proceed to the next step
   * Validates required conditions for each step
   *
   * @returns {boolean} Whether navigation is allowed
   */
  const canProceed = () => {
    // Video mode has 5 steps: Upload(0) -> Trim(1) -> Configure(2) -> Preview(3) -> Download(4)
    // Image mode has 4 steps: Upload(0) -> Configure(1) -> Preview(2) -> Download(3)
    if (step === 0) return files.length > 0;

    if (isVideoMode) {
      if (step === 1) {
        // Trim step - always can proceed (trimRange has defaults)
        return files.some(f => f.trimRange || f.maxBlurDuration);
      }
      if (step === 2) {
        // Configure step - check if any person has blur enabled
        return files.some(f => Object.values(f.blurSettings || {}).some(s => s?.enabled));
      }
      if (step === 3) {
        // Preview step - check if processing is complete
        return files.some(f => f.processed || f.downloadUrl);
      }
    } else {
      if (step === 1) {
        // Image Configure - check blur settings or custom boxes
        const hasEnabledBlurSettings = files.some(f => Object.values(f.blurSettings || {}).some(s => s?.enabled));
        const hasEnabledCustomBoxes = files.some(f => (f.customBlurBoxes || []).some(b => b?.enabled));
        return hasEnabledBlurSettings || hasEnabledCustomBoxes;
      }
    }
    return true;
  };

  /* ========================================================================
     Effects - Download & Analytics Tracking
     ======================================================================== */

  /**
   * Track when all files have been downloaded
   * Updates completion status for UI feedback
   */
  useEffect(() => {
    if (downloadCount >= files.length && files.length > 0 && !allDownloaded) {
      setAllDownloaded(true);
    }
  }, [downloadCount, files.length, allDownloaded]);

  /**
   * Log analytics when user reaches download page
   * Tracks conversion funnel completion
   */
  useEffect(() => {
    // Download step is 3 for images, 4 for videos
    const downloadStep = isVideoMode ? 4 : 3;
    if (step === downloadStep && files.length > 0) {
      files.forEach(file => {
        if (file.sessionId && file.fileId) {
          fetch(`${API_URL}/log-download-page/${file.sessionId}/${file.fileId}`, {
            method: 'POST'
          }).catch(err => console.error('Failed to log download page visit:', err));
        }
      });
    }
  }, [step, files, isVideoMode]);

  /* ========================================================================
     Render
     ======================================================================== */

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 text-white">
      {/* ================================================================
          Header Section
          - App branding and AI-powered badge
          ================================================================ */}
      <header className="border-b border-white/10 flex-shrink-0">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold">LetsBlur</h1>
              <p className="text-xs text-gray-400">AI Privacy Protection</p>
            </div>
          </div>
          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full">
            🤖 AI-Powered
          </span>
        </div>
      </header>

      {/* ================================================================
          Main Content Section
          - Step indicator
          - Processing overlay (when active)
          - Step-specific content components
          ================================================================ */}
      <main className="container mx-auto px-4 py-4 max-w-6xl flex-1 flex flex-col overflow-x-hidden">
        {/* Step Indicator - Hide on step 0 unless processing, show Single Click indicator for that flow */}
        {isSingleClickBlur ? (
          <SingleClickBlurStepIndicator isComplete={oneClickComplete} />
        ) : (step > 0 || isProcessing) && (
          <StepIndicator currentStep={step} allDownloaded={allDownloaded} steps={isVideoMode ? VIDEO_STEPS : STEPS} />
        )}

        {/* Processing Overlay - Shows during AI detection or Single Click Blur completion */}
        {(isProcessing || oneClickComplete) && (
          <ProcessingOverlay
            status={processingStatus}
            isComplete={oneClickComplete}
            completeTitle={oneClickStats.blurred > 0 ? 'Single Click Blur Complete!' : 'No Faces Detected'}
            stats={oneClickStats.blurred > 0 ? oneClickStats : null}
            onComplete={handleOneClickComplete}
          />
        )}

        {/* Step Content - Renders appropriate component based on current step */}
        {!isProcessing && !oneClickComplete && (
          <>
            {/* Step 0: Upload - File selection and validation */}
            {step === 0 && (
              <>
                <UploadStep
                  files={files}
                  onFilesChange={setFiles}
                  maxSizeMB={isVideoMode ? config.max_video_size_mb : config.max_image_size_mb}
                  maxFilesPerUpload={isVideoMode ? 1 : config.max_files_per_upload}
                  maxFilesPerDay={config.max_files_per_day}
                  remainingUploads={remainingUploads}
                  onShowToast={showToast}
                  uploadMode={uploadMode}
                  onUploadModeChange={setUploadMode}
                />
                {/* Single Click Blur / Continue buttons - Always show for images on step 0 */}
                {!isVideoMode && (
                  <OneClickBlur
                    files={files}
                    onShowToast={showToast}
                    onClearAll={() => {
                      files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
                      setFiles([]);
                    }}
                    canProceed={canProceed()}
                    onContinue={nextStep}
                    onOneClickBlur={handleOneClickBlur}
                  />
                )}
              </>
            )}

            {/* ============ IMAGE MODE STEPS ============ */}
            {/* Image Step 1: Configure - Blur settings and box manipulation */}
            {step === 1 && !isVideoMode && (
              <ConfigureStep
                files={files}
                currentFileIndex={currentFileIndex}
                onFileIndexChange={setCurrentFileIndex}
                onFilesChange={setFiles}
                bulkGender={bulkGender}
                onBulkGenderChange={setBulkGender}
                bulkLevel={bulkLevel}
                onBulkLevelChange={setBulkLevel}
                bulkTarget={bulkTarget}
                onBulkTargetChange={setBulkTarget}
                reanalyzeUsed={reanalyzeUsed}
                onReanalyze={handleReanalyze}
                remainingUploads={remainingUploads}
                onShowToast={showToast}
                hasApplied={hasAppliedBlur}
                onHasAppliedChange={setHasAppliedBlur}
              />
            )}

            {/* Image Step 2: Preview - Watermarked preview of results */}
            {step === 2 && !isVideoMode && (
              <PreviewStep
                files={files}
                currentFileIndex={currentFileIndex}
                onFileIndexChange={setCurrentFileIndex}
              />
            )}

            {/* Image Step 3: Download - Final file downloads */}
            {step === 3 && !isVideoMode && (
              <DownloadStep
                files={files}
                downloadCount={downloadCount}
                onDownload={handleDownload}
                onDownloadAll={handleDownloadAll}
                onReset={handleReset}
                allDownloaded={allDownloaded}
                isVideoMode={isVideoMode}
              />
            )}

            {/* ============ VIDEO MODE STEPS ============ */}
            {/* Video Step 1: Trim - Select video portion to process */}
            {step === 1 && isVideoMode && files[currentFileIndex] && (
              <VideoTrimStep
                file={files[currentFileIndex]}
                onFileChange={(updater) => {
                  setFiles(prev => prev.map((f, idx) =>
                    idx === currentFileIndex ? (typeof updater === 'function' ? updater(f) : updater) : f
                  ));
                }}
                onShowToast={showToast}
              />
            )}

            {/* Video Step 2: Configure - Detection and blur settings */}
            {step === 2 && isVideoMode && files[currentFileIndex] && (
              <VideoConfigureStep
                file={files[currentFileIndex]}
                fileIndex={currentFileIndex}
                onFileChange={(updater) => {
                  setFiles(prev => prev.map((f, idx) =>
                    idx === currentFileIndex ? (typeof updater === 'function' ? updater(f) : updater) : f
                  ));
                }}
                onShowToast={showToast}
                bulkGender={bulkGender}
                onBulkGenderChange={setBulkGender}
                bulkLevel={bulkLevel}
                onBulkLevelChange={setBulkLevel}
                bulkTarget={bulkTarget}
                onBulkTargetChange={setBulkTarget}
              />
            )}

            {/* Video Step 3: Preview - Video preview with blur applied */}
            {step === 3 && isVideoMode && files[currentFileIndex] && (
              <VideoPreviewStep
                file={files[currentFileIndex]}
                fileIndex={currentFileIndex}
                onFileChange={(updater) => {
                  setFiles(prev => prev.map((f, idx) =>
                    idx === currentFileIndex ? (typeof updater === 'function' ? updater(f) : updater) : f
                  ));
                }}
                onShowToast={showToast}
              />
            )}

            {/* Video Step 4: Download - Final video download */}
            {step === 4 && isVideoMode && (
              <DownloadStep
                files={files}
                downloadCount={downloadCount}
                onDownload={handleDownload}
                onDownloadAll={handleDownloadAll}
                onReset={handleReset}
                allDownloaded={allDownloaded}
                isVideoMode={isVideoMode}
              />
            )}
          </>
        )}

        {/* ================================================================
            Navigation Controls
            - Back and Continue buttons
            - Disabled states based on validation
            - Note: Step 0 (Upload) uses OneClickBlur component for image mode
            ================================================================ */}
        {/* Navigation - different max step for video (4) vs image (3) mode */}
        {/* Hide default navigation on step 0 (handled separately below) */}
        {!isProcessing && !oneClickComplete && step > 0 && step < (isVideoMode ? 4 : 3) && (
          <div className="flex justify-center items-center gap-4 mt-4">
            <button
              onClick={prevStep}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              ← Back
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed rounded-xl font-medium transition-colors"
            >
              {step === (isVideoMode ? 3 : 2) ? 'Download' : 'Continue'} →
            </button>
          </div>
        )}
        {/* Step 0 navigation: Show Continue button for videos only */}
        {/* Note: Images use OneClickBlur component which handles both buttons */}
        {!isProcessing && !oneClickComplete && step === 0 && isVideoMode && (
          <div className="flex justify-center items-center gap-4 mt-4">
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed rounded-xl font-medium transition-colors"
            >
              Continue →
            </button>
          </div>
        )}
      </main>

      {/* ================================================================
          Footer Section
          - Contact link for custom solutions
          - Copyright notice
          ================================================================ */}
      <footer className="border-t border-white/5 mt-auto py-3 text-center text-xs text-gray-500 flex-shrink-0">
        <div className="mb-1">
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            Contact Us for Custom Solutions
          </button>
        </div>
        <div>
          © {new Date().getFullYear()} LetsBlur • AI-Powered Privacy Protection
        </div>
      </footer>

      {/* ================================================================
          Global UI Components
          - Toast notifications
          - Contact modal
          ================================================================ */}
      <ToastContainer
        toasts={toasts}
        onDismiss={(id) => setToasts(prev => prev.filter(t => t.id !== id))}
        onDismissAll={() => setToasts([])}
      />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}
